import React from 'react';
import PropTypes from 'prop-types';
import { colorGrid } from 's2s-themes';
import styled from 'styled-components';
import DialingInput from '../dialingInput';
import Keypad from '../keypad/keypad';
import Button from 's2s-button';
import { compose } from 'recompose';
import { injectIntl } from 'react-intl';
import { getIntlText, debounce } from '../../js/utilities';

const composedHoc = compose(injectIntl);
const ComposedButton = composedHoc(Button);
const ComposedKeypad = composedHoc(Keypad);
const ComposedDialingInput = composedHoc(DialingInput);

const UserActions = styled.div`
  width : 100%;
`;

const FooterControls = styled.div`
  align-items: center;
  background-color: ${colorGrid.gray0};
  border-top: 1px solid ${colorGrid.gray3};
  display: flex;
  font-size: 14px;
  height: 56px;
  justify-content: space-between;
  padding-left: 16px;
  padding-right: 16px;
`;

const CancelTransferButton = styled(ComposedButton)`
  background-color: ${colorGrid.gray0};
  border: 1px solid #a7b4e9;
  cursor: pointer;
  color: #243991;
  display: inline-flex;
  font-family: Open Sans;
  font-weight: 700;
  justify-content : center;
  letter-spacing: 1.25px;
  margin-right: 16px;
  text-transform: uppercase;
  width: 33%;

  &:hover {
    background-color: #d3d9f4;
  }
`;

const BlindTransferButton = styled(ComposedButton)`
  background-color: ${({ isDisabled }) => isDisabled ? '#e5e6e8' : '#29a3d2'};
  display: inline-flex;
  font-family: Open Sans;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  width: 33%;
  
  &:hover {
    background-color: ${({ isDisabled }) => isDisabled ? '#e5e6e8' : '#155269'};
  }
  
  div {
    color: ${({ isDisabled }) => isDisabled ? '#4c5863' : colorGrid.gray0};
  }
`;

const AttendedTransferButton = styled(BlindTransferButton)`
  background-color: ${({ isDisabled }) => isDisabled ? '#e5e6e8' : '#099f24'};
  font-family: Open Sans;
  font-weight: 700;
  letter-spacing: 1.25px;
  margin-left: 16px;
  text-transform: uppercase;
  
  &:hover {
    background-color: ${({ isDisabled }) => isDisabled ? '#e5e6e8' : '#066a18'};
  }
  
  div {
    color: ${({ isDisabled }) => isDisabled ? '#4c5863' : colorGrid.gray0};
  }
`;

const InitiateTransferUserControls = (props) => {
  const {
      dialingInputValue,
      cbHandleKeyPress,
      cbHandleBackspaceClick,
      cbHandleDialingInputChange,
      cbHandleKeypadClick,
      dtmfOutputDeviceId,
      disableDTMFTone,
      cbHandleCancelTransferClick,
      intl,
      cbHandleBlindTransferClick,
      cbHandleAttendedTransferClick,
      inprogressCalls,
  } = props;

  const isDialing = Object.values(inprogressCalls).some(({ userControlStatus }) => userControlStatus === 'dialing');
  const buttonsDisabled = isDialing || !dialingInputValue.replace(/\s/img, '');

  console.log('PROPS IntiateTransfer', dialingInputValue);
  return (
    <UserActions
      className="IntiateTransfer UserActions"
      onKeyPress={cbHandleKeyPress}
    >
      <ComposedDialingInput
        cbBackspaceClick={cbHandleBackspaceClick}
        cbChange={cbHandleDialingInputChange}
        value={dialingInputValue}
      />
      <ComposedKeypad
        cbKeyPress={cbHandleKeypadClick}
        dtmfOutputDeviceId={dtmfOutputDeviceId}
        disableDTMFTone={disableDTMFTone}
      />
      <FooterControls className="FooterControls">
        <CancelTransferButton
          buttonLabel={getIntlText(intl, 'CANCEL-BTN-LABEL', 'Cancel')}
          buttonSize="large"
          buttonType="secondary"
          cbClick={cbHandleCancelTransferClick}
        />
        <BlindTransferButton
          buttonLabel={getIntlText(intl, 'BLIND-BTN-LABEL', "Blind")}
          buttonSize="large"
          buttonType="primaryBlue"
          isDisabled={buttonsDisabled}
          cbClick={debounce(cbHandleBlindTransferClick, 1000, true)}
        />
        <AttendedTransferButton
          buttonLabel={getIntlText(intl, 'ATTENDED-BTN-LABEL', "Attended")}
          buttonSize="large"
          buttonType="primaryGreen"
          isDisabled={buttonsDisabled}
          cbClick={debounce(cbHandleAttendedTransferClick, 1000, true)}
        />
      </FooterControls>
    </UserActions>
  );
};

InitiateTransferUserControls.propTypes = {
  intl: PropTypes.object,
  inprogressCalls: PropTypes.object,
  cbHandleKeyPress: PropTypes.func,
  cbHandleBackspaceClick: PropTypes.func,
  cbHandleDialingInputChange: PropTypes.func,
  dialingInputValue: PropTypes.string,
  cbHandleKeypadClick: PropTypes.func,
  cbHandleCancelTransferClick: PropTypes.func,
  cbHandleBlindTransferClick: PropTypes.func,
  cbHandleAttendedTransferClick: PropTypes.func,
  dtmfOutputDeviceId : PropTypes.string,
  disableDTMFTone: PropTypes.bool,
};

InitiateTransferUserControls.defaultProps = {
  inprogressCalls: {},
  cbHandleKeyPress: () => undefined,
  cbHandleBackspaceClick: () => undefined,
  cbHandleDialingInputChange: () => undefined,
  dialingInputValue: '',
  cbHandleKeypadClick: () => undefined,
  cbHandleCancelTransferClick: () => undefined,
  cbHandleBlindTransferClick: () => undefined,
  cbHandleAttendedTransferClick: () => undefined,
  dtmfOutputDeviceId : '',
  disableDTMFTone: true,
};

export default injectIntl(InitiateTransferUserControls);
